/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    name: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    wordmarkPink: string;  // Cr8Her wordmark, pink (light + noir themes)
    wordmarkWhite: string; // Cr8Her wordmark, white (crimson theme)
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    device?: {
      ios: string;        // Color for iOS line in device breakdown charts
      android: string;    // Color for Android line in device breakdown charts
    };
    gender?: {
      male: string;       // Color for male in gender breakdown charts
      female: string;     // Color for female in gender breakdown charts
    };
    bar?: string;         // Performance bar color (appears black/dark)
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    name: "Cr8Her",
    tagline: "Sales Dashboard",
    copyright: "Cr8Her",
  },

  logos: {
    primary: "/cr8her-logo.png",
    wordmark: "/cr8her-logo.png",
    wordmarkPink: "/cr8her-wordmark-pink.svg?v=20261019",
    wordmarkWhite: "/cr8her-wordmark-white.svg?v=20261019",
    favicon: "/favicon.ico",
  },

  colors: {
    // Deep rose / mauve palette
    primary: "#9C4666",      // Deep rose — buttons, active states
    secondary: "#C47090",    // Lighter rose — gradient start
    tertiary: "#7A3352",     // Darker rose — gradient end
    accent: "#F5D5E0",       // Pale rose — highlights, badges
    ring: "#9C4666",         // Focus ring

    gradient: {
      start: "#C47090",
      end: "#7A3352",
      angle: 135,
    },

    chart: [
      "#9C4666",  // deep rose — primary series
      "#C47090",  // lighter rose — secondary series
      "#7A3352",  // darker rose — tertiary
      "#B85C7A",  // medium rose
      "#E0A0B8",  // pale rose
      "#6B2845",  // deep dark rose
      "#D48098",  // muted rose
      "#F0C0D0",  // very pale rose
    ],

    device: {
      ios:     "#9C4666",
      android: "#7A3352",
    },

    gender: {
      male:   "#B85C7A",
      female: "#9C4666",
    },

    bar: "#111827",

    background: {
      light: "#fdf8f9",
      dark: "#0a0a0a",
    },

    card: {
      light: "#ffffff",
      dark: "#171717",
    },
  },

  email: {
    headerGradientStart: "#C47090",
    headerGradientEnd: "#7A3352",
    buttonBackground: "#9C4666",
    accentText: "#9C4666",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors (injected after theme detection below)

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Update background based on current theme
  const isDark = root.classList.contains("dark");
  const isCrimson = root.classList.contains("crimson");
  const isNoir = root.classList.contains("noir");
  // Set background and card based on current theme
  // Must use inline style (not CSS class) to win the specificity battle
  if (isNoir) {
    root.style.setProperty("--brand-primary", "#ffffff");
    root.style.setProperty("--brand-secondary", "#cccccc");
    root.style.setProperty("--brand-tertiary", "#555555");
    root.style.setProperty("--brand-accent", "#333333");
    root.style.setProperty("--brand-ring", "#ffffff");
    root.style.setProperty("--brand-gradient-start", "#cccccc");
    root.style.setProperty("--brand-gradient-end", "#444444");
    root.style.setProperty("--primary", "#ffffff");
    root.style.setProperty("--ring", "#ffffff");
    ["#ffffff", "#cccccc", "#999999", "#666666", "#444444"].forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
    root.style.setProperty("--background", "#0a0a0a");
    root.style.setProperty("--card", "#111111");
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
  } else if (isCrimson) {
    root.style.setProperty("--background", "transparent");
    root.style.setProperty("--card", "rgba(255, 255, 255, 0.93)");
    root.style.setProperty("--foreground", "#ffffff");
    root.style.setProperty("--card-foreground", "#2a0d15");
    root.style.setProperty("--popover", "rgba(255, 255, 255, 0.97)");
    root.style.setProperty("--popover-foreground", "#2a0d15");
    root.style.setProperty("--border", "rgba(255, 255, 255, 0.28)");
    root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.75)");
  } else if (isDark) {
    root.style.setProperty("--background", "transparent");
    root.style.setProperty("--card", cardDark);
    // Green chart palette for dark mode
    const darkChartColors = ["#9B8EC4", "#C8BDDF", "#7A6BAD", "#B5AAD8", "#877ABB"];
    darkChartColors.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
  } else {
    // Default chart colors from branding config
    colors.chart.forEach((color, i) => {
      root.style.setProperty(`--chart-${i + 1}`, color);
    });
    root.style.setProperty("--background", bgLight);
    root.style.setProperty("--card", cardLight);
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
    root.style.removeProperty("--popover");
    root.style.removeProperty("--popover-foreground");
    root.style.removeProperty("--border");
    root.style.removeProperty("--muted-foreground");
  }
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
